import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  withRouter,
  Redirect,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import get from 'lodash/get';
import {
  SAMLAuthenticate,
  setAuthenticating,
  logout,
} from 'AppSrc/core/auth/auth-actions';
import { makeRelayStateFromRedirectRoute } from 'AppSrc/core/auth/auth-functions';
import {
  SAMLAuthUrlSelector,
  isSAMLAuthUrlFetchingSelector,
  hasSAMLAuthErrorSelector,
  isAuthenticatingSelector,
  isTokenCheckingSelector,
  authenticatedSelector,
} from 'AppSrc/core/auth/auth-selectors';
import { VodafoneLogo } from 'AppSrc/core/components/common/svg-icons';
import 'AppSrc/core/style/components/auth/login-page.css';
import loginBackground from 'AppSrc/core/images/login-background.png';
import { GridOverlay } from 'AppSrc/core/components/dev-page/components/grid-overlay';
import { LoadingSpinner } from 'AppSrc/core/components/utility/loading-spinner';


const showGridOverlay = process.env.NODE_ENV === 'development';


const LoginPageDisplay = React.memo(({
  match,
  history,
  location,
  referer,
  authenticated,
  SAMLAuthUrl,
  isSAMLAuthUrlFetching,
  hasSAMLAuthError,
  isAuthenticating,
  isTokenChecking,
  getSAMLAuthUrl,
  flagSAMLAuthInProgress,
  tokenExpired,
  clearCurrentLogin,
}) => {

  // get SAML auth Url
  useEffect(
    () => {
      // !!! DO NOT get the SAML AUTH URL while a token found in local storage is being checked for validity !!!
      // it won't save time, the checked token is on axios auth header so the SAML url request will fail as it need auth header empty
      if (!isTokenChecking && !authenticated && !SAMLAuthUrl && !isSAMLAuthUrlFetching && !hasSAMLAuthError) {
        console.log('LOGIN USE EFFECT getSAMLAuthUrl !!!!');
        getSAMLAuthUrl();
      }
    },
    [isTokenChecking, SAMLAuthUrl, isSAMLAuthUrlFetching, hasSAMLAuthError, authenticated, getSAMLAuthUrl],
  );

  // if we have been redirected here due to an expired token, we should log the user out on first mount
  useEffect(
    () => {
      if (tokenExpired) {
        clearCurrentLogin();
      }
    },
    [tokenExpired, clearCurrentLogin],
  );

  const submitLogin = () => {
    flagSAMLAuthInProgress();
    // visit the 3rd party SAML link url
    // MM: keep jest happy: window.location.assign(url) instead of window.location.href = url
    // http://xxd3vin.github.io/2018/03/13/error-not-implemented-navigation-except-hash-changes.html
    window.location.assign(SAMLAuthUrl);
  };

  const { from } = referer.location.state || { from: { pathname: '/' } };
  if (authenticated === true) {
    return <Redirect to={from} />;
  }

  const backGroundImageStyle = {
    // I couldn't get the image to import correctly using scss so I had to use inline instead :shrugs:
    backgroundImage: `url(${loginBackground})`,
  };

  return (
    <div className="login-page">

      {
        showGridOverlay && (
          <GridOverlay />
        )
      }

      <div className="login-page-inner">

        <div className="login-image-container">
          <div
            className="login-image"
            style={backGroundImageStyle}
          />
        </div>

        <div className="login-ui-container">
          <div className="login-ui">

            <div className="login-brand">
              <div className="login-brand-svg">
                <VodafoneLogo />
              </div>
              <p className="login-brand-intro">Welcome to the</p>
              <h1 className="login-brand-title">ACE Portal</h1>
            </div>

            {
              !SAMLAuthUrl && isSAMLAuthUrlFetching && (
                <LoadingSpinner />
              )
            }

            {
              hasSAMLAuthError && (
                <p className="login-error">
                  The authentication server is down. Please try reload the app in a few minutes.
                </p>
              )
            }

            {
              SAMLAuthUrl && (
                <>
                  <div className="login-button-wrapper">
                    <button
                      type="button"
                      className="login-button"
                      onClick={submitLogin}
                    >
                      LOGIN
                    </button>
                  </div>
                </>
              )
            }
          </div>
        </div>

      </div>

      <div className="login-page-footer" />

    </div>
  );
});

LoginPageDisplay.propTypes = {
  match: ReactRouterPropTypes.match,
  history: ReactRouterPropTypes.history,
  location: ReactRouterPropTypes.location,
  referer: ReactRouterPropTypes.route,
  authenticated: PropTypes.bool,
  SAMLAuthUrl: PropTypes.string,
  isSAMLAuthUrlFetching: PropTypes.bool,
  isTokenChecking: PropTypes.bool,
  hasSAMLAuthError: PropTypes.bool,
  isAuthenticating: PropTypes.bool,
  getSAMLAuthUrl: PropTypes.func,
  flagSAMLAuthInProgress: PropTypes.func,
  tokenExpired: PropTypes.bool,
  clearCurrentLogin: PropTypes.func,
};


/* --- LoginPage Redux-connected container component --- */

const mapStateToProps = (state, ownProps) => ({
  authenticated: authenticatedSelector(state),
  SAMLAuthUrl: SAMLAuthUrlSelector(state),
  isSAMLAuthUrlFetching: isSAMLAuthUrlFetchingSelector(state),
  isAuthenticating: isAuthenticatingSelector(state),
  isTokenChecking: isTokenCheckingSelector(state),
  hasSAMLAuthError: hasSAMLAuthErrorSelector(state),
});

// mapDispatchToProps tells the container component how to dispatch actions to the redux store
const mapDispatchToProps = (dispatch, ownProps) => ({
  getSAMLAuthUrl: () => {
    // MM: when you load the app direclty on /login IN A NEW TAB instead of being redirected to it from /
    // the referrer is not set properly and trying to get the pathname crashes
    // reloading on login in an existing tab does not trigger this problem
    // assume desired route is / if referrer not set
    const redirectRoute = get(ownProps, 'referer.location.state.from.pathname', '/');
    const relayState = makeRelayStateFromRedirectRoute(redirectRoute);
    dispatch(SAMLAuthenticate(relayState));
  },
  flagSAMLAuthInProgress: () => {
    console.log('flagSAMLAuthInProgress');
    dispatch(setAuthenticating());
  },
  clearCurrentLogin: () => {
    dispatch(logout());
  },
});

const LoginPage = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(LoginPageDisplay));

export {
  LoginPageDisplay,
  LoginPage,
};







